import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { isStaleChunkError, reloadOnceForStaleChunk } from '@/lib/stale-chunk';
import { cn } from '@/lib/utils';

interface ErrorCardProps {
  heading: string;
  message: string;
  /** `code` is the big status code (403, 404, !); `title` is a sentence-length heading. */
  headingSize: 'code' | 'title';
  children?: ReactNode;
}

/**
 * The centred error card, with no router dependency (no `Link`, no router hook),
 * so it can render outside `RouterProvider` too: the Tor block page replaces
 * the whole router (#118 R35).
 *
 * A sentence-length `title` is the page's real `h1` (the Tor block page is the
 * whole document, so a screen reader must land on it as the heading); a
 * status `code` keeps its paragraph, so the other error pages look and read
 * exactly as before.
 */
export function ErrorCard({ heading, message, headingSize, children }: ErrorCardProps) {
  const Heading = headingSize === 'title' ? 'h1' : 'p';
  return (
    <div className="grid min-h-dvh place-items-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm">
        <Heading className={cn('font-display font-bold text-ingazi-indigo', headingSize === 'code' ? 'text-5xl' : 'text-2xl')}>{heading}</Heading>
        <p className="mt-3 text-sm text-muted-foreground">{message}</p>
        {children}
      </div>
    </div>
  );
}

function Shell({ code, message }: { code: string; message: string }) {
  const { t } = useTranslation();
  return (
    <ErrorCard heading={code} message={message} headingSize="code">
      <Button asChild variant="brand" className="mt-6">
        <Link to="/">{/* Back to dashboard */ t('common.goHome')}</Link>
      </Button>
    </ErrorCard>
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation();
  return <Shell code="403" message={/* You don’t have access to this page. */ t('common.forbidden')} />;
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return <Shell code="404" message={/* Page not found. */ t('common.notFound')} />;
}

/** Router `errorElement` — renders whatever a route threw, without white-screening. */
export function RouteError() {
  const { t } = useTranslation();
  const error = useRouteError();

  useEffect(() => {
    if (isStaleChunkError(error)) reloadOnceForStaleChunk();
  }, [error]);

  const message = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : /* Something went wrong. */ t('common.somethingWrong');
  return <Shell code="!" message={message} />;
}
